import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** When provided together with itemsPerPage, shows a "Showing X-Y of Z" label. */
  totalItems?: number;
  itemsPerPage?: number;
  className?: string;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  className = '',
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const showSummary = typeof totalItems === 'number' && typeof itemsPerPage === 'number';
  const rangeStart = showSummary ? (currentPage - 1) * itemsPerPage! + 1 : null;
  const rangeEnd = showSummary ? Math.min(currentPage * itemsPerPage!, totalItems!) : null;

  return (
    <div className={`flex items-center justify-between px-1 py-3 ${className}`}>
      {showSummary ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Showing <span className="font-medium text-gray-700 dark:text-gray-300">{rangeStart}</span>–
          <span className="font-medium text-gray-700 dark:text-gray-300">{rangeEnd}</span> of{' '}
          <span className="font-medium text-gray-700 dark:text-gray-300">{totalItems}</span>
        </p>
      ) : (
        <span />
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Previous
        </button>
        <span className="text-sm text-gray-600 dark:text-gray-400 tabular-nums">
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Next
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Client-side pagination helper: slices `items` to the current page. Mirrors HistoryPanel's approach. */
export function paginate<T>(items: T[], currentPage: number, itemsPerPage: number): T[] {
  const start = (currentPage - 1) * itemsPerPage;
  return items.slice(start, start + itemsPerPage);
}
